import './lib/migrateStorage';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { appName, setAppName } from '@boltcrm/module-sdk/web';
import { installMotionInput, TooltipProvider } from '@boltcrm/ui';
import '@boltcrm/ui/tokens.css';
import '@boltcrm/ui/base.css';
import '@boltcrm/ui/style-promax.css';
import { applyStyle, applyTheme, followSystemTheme } from './lib/theme';

applyTheme(undefined, { remember: false });
applyStyle();
followSystemTheme();
// Keyboard-initiated actions never animate (ADR-1033): note whether the last input was a key or a pointer.
installMotionInput();

/**
 * The product name comes from the server (`APP_NAME`, ADR-860): fetch it before anything renders, so every string,
 * the logo and the page title use the operator's name. If the API doesn't answer, the built-in name stays.
 */
async function loadAppName(): Promise<void> {
  try {
    const res = await fetch('/api/public-config', { credentials: 'include' });
    if (res.ok) setAppName(((await res.json()) as { data?: { appName?: string } }).data?.appName);
  } catch {
    /* offline or the API is starting: keep the default name */
  }
}

async function start(): Promise<void> {
  await loadAppName();
  // Imported after the name is set: some modules build labels from `t()` when they load.
  const { createAppRouter } = await import('./router');
  document.title = appName();

  const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: true } } });
  const router = createAppRouter(queryClient);

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <RouterProvider router={router} />
        </TooltipProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

void start();
